import { IconCheck, IconX } from '@tabler/icons-react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface InlineEditableTextProps {
  value: string;
  onSubmit: (next: string) => Promise<void> | void;
  isReadOnly?: boolean;
  maxLength?: number;
  ariaLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  displayClassName?: string;
  inlineSuffix?: ReactNode;
  inputStyle?: CSSProperties;
}

const DEFAULT_MAX_LENGTH = 300;

export const InlineEditableText = ({
  value,
  onSubmit,
  isReadOnly = false,
  maxLength = DEFAULT_MAX_LENGTH,
  ariaLabel,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  placeholder,
  className,
  inputClassName,
  displayClassName,
  inlineSuffix,
  inputStyle,
}: InlineEditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // EWM-1041: `draft` used to be kept in step with the `value` prop by a second effect here.
  // It is only ever read while editing, so it is now seeded by the event that enters edit
  // mode (`startEditing` below). The effect re-ran on every `isEditing` flip too, and cost an
  // extra commit each time the prop changed while the field was idle — which is every query
  // refetch, on every mounted instance, in the per-row callers (EstimationItemsTable,
  // ResourceColumnHeader, ProjectPhases).

  // Focus and select the input when edit mode opens.
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const trimmed = draft.trim();
  const isInvalid = trimmed.length === 0 || trimmed.length > maxLength;
  const isUnchanged = trimmed === value.trim();

  const startEditing = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const exit = () => {
    setIsEditing(false);
    setDraft(value);
  };

  const commit = async () => {
    if (isInvalid || isSubmitting) return;
    if (isUnchanged) {
      setIsEditing(false);
      return;
    }
    try {
      setIsSubmitting(true);
      await onSubmit(trimmed);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      exit();
    }
  };

  if (isReadOnly) {
    return (
      <span className={`${className ?? ''} ${displayClassName ?? ''}`}>
        {value}
        {inlineSuffix}
      </span>
    );
  }

  if (!isEditing) {
    return (
      <button
        type='button'
        onClick={startEditing}
        className={`group inline-flex max-w-full items-baseline text-left ${className ?? ''}`}
        aria-label={ariaLabel ?? `Rename ${value}`}
      >
        <span
          className={`truncate group-hover:underline group-focus-visible:underline ${displayClassName ?? ''}`}
        >
          {value}
        </span>
        {inlineSuffix}
      </button>
    );
  }

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 ${className ?? ''}`}
      onClick={(event) => event.stopPropagation()}
    >
      <input
        ref={inputRef}
        type='text'
        value={draft}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-label={ariaLabel ?? 'Rename'}
        aria-invalid={isInvalid}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        // Click outside cancels per EWM-589 / EWM-590 spec. The confirm/cancel buttons
        // use onMouseDown + preventDefault to keep focus on the input, so onBlur only
        // fires on genuine outside-clicks.
        onBlur={exit}
        disabled={isSubmitting}
        className={`border-c4 dark:bg-c16 min-w-0 flex-1 rounded border bg-white px-2 py-1 text-sm text-black dark:text-white ${inputClassName ?? ''}`}
        style={inputStyle}
      />
      <span className={`text-xs ${isInvalid ? 'text-danger' : 'text-muted'}`} aria-live='polite'>
        {draft.length}/{maxLength}
      </span>
      <button
        type='button'
        // onMouseDown — fire before the input's onBlur so the click is registered as confirm rather than cancel.
        onMouseDown={(event) => {
          event.preventDefault();
          void commit();
        }}
        disabled={isInvalid || isSubmitting}
        aria-label={confirmLabel}
        title={confirmLabel}
        className='text-success hover:bg-c10 dark:hover:bg-c4 inline-flex items-center justify-center rounded p-1 disabled:cursor-not-allowed disabled:opacity-40'
      >
        <IconCheck size={16} aria-hidden />
      </button>
      <button
        type='button'
        onMouseDown={(event) => {
          event.preventDefault();
          exit();
        }}
        disabled={isSubmitting}
        aria-label={cancelLabel}
        title={cancelLabel}
        className='text-danger hover:bg-c10 dark:hover:bg-c4 inline-flex items-center justify-center rounded p-1 disabled:cursor-not-allowed disabled:opacity-40'
      >
        <IconX size={16} aria-hidden />
      </button>
      {inlineSuffix}
    </span>
  );
};

export default InlineEditableText;
